/**
 * calendarLogic.js — pure, framework-free logic for the Reservation Operations
 * Calendar (/calendar) and the sidebar/dashboard alert badge.
 *
 * Extracted verbatim from pages/Calendar.jsx and services/api.js so the logic is
 * unit-testable under plain `node` (no React, no DOM, no network). Behaviour is
 * identical to the previously-inlined versions — this is a relocation, not a
 * rewrite. All dates are LOCAL `YYYY-MM-DD` strings (no UTC shifting); `today` is
 * always the caller's local `YYYY-MM-DD`.
 */
import { getBookingReference, getOperationalBookingReference } from './bookingReference.js';

// ---- local date helpers (no UTC shifting) ----------------------------------
export const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const parseYmd = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };

// arrivals/departures are history once past — never "overdue work".
export const OVERDUE_TYPES = new Set(['guest_payment_due', 'insurance_due', 'security_refund_due', 'owner_payout_pending', 'hold_expiry']);
export const isTask = (e) => e?.type === 'task';
export const isOverdueEvent = (e, today) => {
  if (!e.start || e.start >= today) return false;
  if (isTask(e)) return e.status !== 'DONE';
  return OVERDUE_TYPES.has(e.type);
};
export const isOpenTask = (e) => isTask(e) && e.status !== 'DONE';

// One pass over the events → every summary-card count. Reuses the same
// overdue/today rules used elsewhere (no duplicated date logic). `today` is the
// caller's local YYYY-MM-DD.
export function getSummaryStats(events, today) {
  const s = {
    overdue: 0, arrivalsToday: 0, departuresToday: 0,
    paymentsDue: 0, paymentsOverdue: 0, refundsDue: 0, refundsOverdue: 0,
    openTasks: 0, ownerPayouts: 0,
  };
  for (const e of events) {
    if (isOverdueEvent(e, today)) s.overdue += 1;                  // excludes arrivals/departures by rule
    if (e.type === 'arrival' && e.start === today) s.arrivalsToday += 1;
    if (e.type === 'departure' && e.start === today) s.departuresToday += 1;
    if (e.type === 'guest_payment_due') { s.paymentsDue += 1; if (e.start && e.start < today) s.paymentsOverdue += 1; }
    if (e.type === 'security_refund_due') { s.refundsDue += 1; if (e.start && e.start < today) s.refundsOverdue += 1; }
    if (isOpenTask(e)) s.openTasks += 1;
    if (e.type === 'owner_payout_pending') s.ownerPayouts += 1;
  }
  return s;
}

// ---- plain-English labels --------------------------------------------------
export const EVENT_LABELS = {
  arrival: 'Guest Arrival',
  departure: 'Guest Departure',
  guest_payment_due: 'Guest Payment Due',
  insurance_due: 'Insurance Due',
  security_refund_due: 'Security Deposit Refund Due',
  owner_payout_pending: 'Owner Payout Pending',
  hold_expiry: 'Hold Expiring',
  task: 'Task',
};
export const getEventLabel = (type) => EVENT_LABELS[type] || (type ? type.replace(/_/g, ' ') : 'Event');

// Record reference shown in the detail modal. guest_payment_due entity_refs carry an
// `installment` number (there is no per-installment invoice/payment record to link to),
// so surface it as "<table> #<id> · Payment N" instead of a bare booking id. All other
// refs are unchanged (no installment → no suffix).
export const formatEntityRef = (entity_ref) => {
  if (!entity_ref || entity_ref.table == null || entity_ref.id == null) return '—';
  const base = `${entity_ref.table} #${entity_ref.id}`;
  return entity_ref.installment != null ? `${base} · Payment ${entity_ref.installment}` : base;
};

// ---- guest_payment_due booking context (detail modal) ----------------------
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymdPart = (s, withYear) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return null;
  return withYear ? `${MONTHS[m - 1]} ${d}, ${y}` : `${MONTHS[m - 1]} ${d}`;
};
// "Aug 11 – Aug 16, 2026" (same year) or "Dec 27, 2026 – Jan 3, 2027" (spans years).
// Locale-independent on purpose so the output is deterministic + testable. Returns null
// if neither date is usable.
export function formatStay(checkIn, checkOut) {
  if (checkIn && checkOut) {
    const sameYear = String(checkIn).slice(0, 4) === String(checkOut).slice(0, 4);
    const ci = ymdPart(checkIn, !sameYear);
    const co = ymdPart(checkOut, true);
    if (ci && co) return `${ci} – ${co}`;
  }
  return ymdPart(checkIn || checkOut, true);
}
// USD, 2 decimals — matches utils/formatters.formatCurrency. Returns null for non-numbers.
export function formatUsd(amount) {
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// "Villa Magnifico (#2214)" when a name is known, else "Villa #2214", else null.
const villaLabel = (ctx, event) => {
  const name = ctx && ctx.villa_name;
  const id = ctx && ctx.villa_id != null ? ctx.villa_id : (event ? event.villa_id : null);
  if (name) return id != null ? `${name} (#${id})` : name;
  return id != null ? `Villa #${id}` : null;
};

// Context-first sections for the detail modal of an OPERATIONAL event (everything except
// task — tasks have their own modal branch). Returns { sections: [{ title, rows:[{label,
// value}] }] }. Each row is included only when its source field exists (graceful omission,
// never blank rows). Reads event.context (set by /api/agenda); falls back to event.villa_id.
export function getEventContext(event) {
  if (!event || event.type === 'task') return { sections: [] };
  const ctx = event.context || {};
  const sections = [];
  const hasBooking = ctx.booking_id != null;

  if (event.type === 'hold_expiry' && !hasBooking) {
    // A villa_blocks hold is NOT linked to a booking — say so rather than guessing.
    const rows = [];
    const villa = villaLabel(ctx, event);
    if (villa) rows.push({ label: 'Villa', value: villa });
    rows.push({ label: 'Linked booking', value: 'None' });
    sections.push({ title: 'Hold Context', rows });
  } else {
    const rows = [];
    const operationalBookingReference = getOperationalBookingReference(ctx);
    if (operationalBookingReference) {
      rows.push({ label: 'Booking', value: operationalBookingReference });
      if (hasBooking) rows.push({ label: 'Internal booking ID', value: `Booking #${ctx.booking_id}` });
    } else if (hasBooking) {
      rows.push({ label: 'Booking', value: getBookingReference(ctx, { fallbackId: ctx.booking_id }) });
    }
    const villa = villaLabel(ctx, event);
    if (villa) rows.push({ label: 'Villa', value: villa });
    if (ctx.guest_name) rows.push({ label: 'Guest', value: ctx.guest_name });
    const stay = formatStay(ctx.check_in, ctx.check_out);
    if (stay) rows.push({ label: 'Stay', value: stay });
    if (ctx.booking_status) rows.push({ label: 'Status', value: ctx.booking_status });
    if (rows.length) sections.push({ title: 'Booking Context', rows });
  }

  // Action Details — only where there's info beyond the title + the top Due Date row.
  const action = [];
  if (event.type === 'guest_payment_due') {
    if (ctx.payment_number != null) action.push({ label: 'Payment', value: `Payment ${ctx.payment_number}` });
    const amt = formatUsd(ctx.amount_due);
    if (amt) action.push({ label: 'Amount Due', value: amt });
  } else if (event.type === 'security_refund_due') {
    action.push({ label: 'Reminder', value: 'Security deposit refund' });
  }
  if (action.length) sections.push({ title: 'Action Details', rows: action });

  return { sections };
}

// ---- filters ---------------------------------------------------------------
export const DEFAULT_FILTERS = { category: 'all', priority: 'all', assignee: 'all', myTasks: false, search: '' };
export const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'guest_movement', label: 'Guest Movement' },
  { value: 'payments', label: 'Payments' },
  { value: 'refunds', label: 'Refunds' },
  { value: 'owner_payouts', label: 'Owner Payouts' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'holds', label: 'Holds' },
];
export const PRIORITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All priorities' },
  { value: 'HIGH', label: 'High' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Low' },
];
const CATEGORY_TYPES = {
  guest_movement: ['arrival', 'departure'],
  payments: ['guest_payment_due', 'insurance_due'],
  refunds: ['security_refund_due'],
  owner_payouts: ['owner_payout_pending'],
  tasks: ['task'],
  holds: ['hold_expiry'],
};

export function matchesCategory(event, category, today) {
  if (!category || category === 'all') return true;
  if (category === 'overdue') return isOverdueEvent(event, today);   // arrivals/departures never overdue
  const types = CATEGORY_TYPES[category];
  return types ? types.includes(event.type) : true;
}
// Priority / assignee only narrow TASK events; operational events always pass (so
// these selections never hide arrivals/payments/etc. unless category is Tasks).
export function matchesPriority(event, priority) {
  if (!priority || priority === 'all') return true;
  if (!isTask(event)) return true;
  return (event.priority || 'NORMAL') === priority;
}
export function matchesAssignee(event, assignee, currentUserId) {
  if (!assignee || assignee === 'all') return true;
  if (!isTask(event)) return true;
  const target = assignee === 'me' ? currentUserId : assignee;
  return String(event.assigned_to || '') === String(target || '');
}
// "My Tasks" is a hard filter: ONLY open tasks assigned to the current user.
export function matchesMyTasks(event, myTasks, currentUserId) {
  if (!myTasks) return true;
  return isOpenTask(event) && String(event.assigned_to || '') === String(currentUserId || '');
}
export function matchesSearch(event, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const ref = event.entity_ref || {};
  const hay = [
    event.title,
    getEventLabel(event.type),
    ref.table,
    ref.id != null ? String(ref.id) : '',
    event.villa_id != null ? String(event.villa_id) : '',
    isTask(event) ? event.description : '',
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q);
}
// Pure: returns a NEW array, never mutates the input.
export function applyCalendarFilters(events, filters, today, currentUserId) {
  if (!Array.isArray(events)) return [];
  return events.filter((e) =>
    matchesCategory(e, filters.category, today)
    && matchesPriority(e, filters.priority)
    && matchesAssignee(e, filters.assignee, currentUserId)
    && matchesMyTasks(e, filters.myTasks, currentUserId)
    && matchesSearch(e, filters.search));
}
export function hasActiveFilters(filters) {
  return filters.category !== 'all' || filters.priority !== 'all'
    || filters.assignee !== 'all' || !!filters.myTasks
    || (filters.search || '').trim() !== '';
}

// ---- alert-badge summary (sidebar Calendar badge + Dashboard card) ----------
// Pure count logic behind services/api.getCalendarAlertSummary(). Counts ONLY
// due-today/overdue items (start <= todayYmd) of the TRUSTED types — task
// (status !== 'DONE'), guest_payment_due, security_refund_due, insurance_due,
// hold_expiry. EXCLUDES arrival/departure/owner_payout_pending and anything in the
// future. todayYmd is the caller's local YYYY-MM-DD. Returns
// { total, tasks, payments, refunds, insurance, holds }.
export function summarizeCalendarAlerts(events, todayYmd) {
  const s = { total: 0, tasks: 0, payments: 0, refunds: 0, insurance: 0, holds: 0 };
  if (!Array.isArray(events)) return s;
  for (const e of events) {
    if (!e || !e.start || e.start > todayYmd) continue;     // future items (except due today) excluded
    if (e.type === 'task') { if (e.status !== 'DONE') { s.tasks += 1; s.total += 1; } }
    else if (e.type === 'guest_payment_due') { s.payments += 1; s.total += 1; }
    else if (e.type === 'security_refund_due') { s.refunds += 1; s.total += 1; }
    else if (e.type === 'insurance_due') { s.insurance += 1; s.total += 1; }
    else if (e.type === 'hold_expiry') { s.holds += 1; s.total += 1; }
    // arrival / departure / owner_payout_pending intentionally NOT counted.
  }
  return s;
}
